import { useState, useEffect, useRef } from "react";
import { X, Loader2 } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import api from "@/lib/api";

export function RSVPModal({ event, onClose }) {
  const [form, setForm] = useState({
    first_name: "",
    last_name: "",
    email: "",
    phone: "",
    guest_count: 1,
    guest_names: [],
    notes: "",
    food_allergies: "",
  });
  const [errors, setErrors] = useState({});
  const [status, setStatus] = useState("idle");
  const [serverError, setServerError] = useState("");
  const firstInputRef = useRef(null);

  useEffect(() => {
    document.body.style.overflow = "hidden";
    const timer = setTimeout(() => firstInputRef.current?.focus(), 100);
    return () => {
      document.body.style.overflow = "";
      clearTimeout(timer);
    };
  }, []);

  useEffect(() => {
    const handleKey = (e) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", handleKey);
    return () => document.removeEventListener("keydown", handleKey);
  }, [onClose]);

  const update = (field) => (e) => {
    const val = e.target.type === "checkbox" ? e.target.checked : e.target.value;
    setForm((f) => {
      const next = { ...f, [field]: val };
      if (field === "guest_count") {
        const count = Math.max(1, Math.min(10, parseInt(val) || 1));
        next.guest_count = count;
        const names = [...(f.guest_names || [])];
        while (names.length < count - 1) names.push("");
        next.guest_names = names.slice(0, count - 1);
      }
      return next;
    });
    if (errors[field]) setErrors((e) => ({ ...e, [field]: null }));
  };

  const updateGuestName = (idx) => (e) => {
    setForm((f) => {
      const names = [...f.guest_names];
      names[idx] = e.target.value;
      return { ...f, guest_names: names };
    });
  };

  const validate = () => {
    const errs = {};
    if (!form.first_name.trim()) errs.first_name = "First name is required";
    if (!form.last_name.trim()) errs.last_name = "Last name is required";
    if (!form.email.trim()) errs.email = "Email is required";
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) errs.email = "Please enter a valid email";
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;
    setStatus("submitting");
    setServerError("");
    try {
      await api.post("/rsvp", {
        event_id: event.id,
        first_name: form.first_name.trim(),
        last_name: form.last_name.trim(),
        email: form.email.trim().toLowerCase(),
        phone: form.phone.trim(),
        guest_count: form.guest_count,
        guest_names: form.guest_names.filter(Boolean),
        notes: form.notes.trim(),
        food_allergies: event.food_served ? form.food_allergies.trim() : "",
        marketing_consent: true,
      });
      setStatus("success");
    } catch (err) {
      setServerError(err.response?.data?.detail || "Something went wrong. Please try again.");
      setStatus("idle");
    }
  };

  const eventDate = event.date
    ? new Date(event.date + "T00:00:00").toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" })
    : "";

  const inputClass = "w-full border border-[#0A1128]/15 px-4 py-2.5 text-sm focus:border-[#0A1128] focus:ring-1 focus:ring-[#0A1128] outline-none transition-colors";
  const labelClass = "block text-xs uppercase tracking-[0.15em] text-[#8A94A6] mb-1.5";

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.25 }}
        className="fixed inset-0 z-[90] bg-[#0A1128]/60 backdrop-blur-sm flex items-center justify-center p-4"
        onClick={onClose}
        role="dialog"
        aria-modal="true"
        aria-labelledby="rsvp-modal-title"
        data-testid="rsvp-modal-overlay"
      >
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: 20 }}
          transition={{ duration: 0.3, ease: "easeOut" }}
          onClick={(e) => e.stopPropagation()}
          className="bg-white w-full max-w-[520px] max-h-[90vh] overflow-y-auto relative"
          data-testid="rsvp-modal-content"
        >
          {/* Close */}
          <button
            onClick={onClose}
            className="absolute top-4 right-4 z-10 w-10 h-10 flex items-center justify-center text-[#8A94A6] hover:text-[#0A1128] transition-colors"
            data-testid="rsvp-modal-close"
          >
            <X size={18} />
          </button>

          <div className="px-6 py-7 md:px-10 md:py-9">
            {status === "success" ? (
              /* Success state */
              <div className="text-center py-8" data-testid="rsvp-success">
                <div className="w-12 h-[1px] bg-[#CBA365] mx-auto mb-6" />
                <h2
                  className="section-heading font-heading text-2xl sm:text-3xl text-[#0A1128] mb-4"
                  style={{ letterSpacing: "-0.01em" }}
                >
                  You're on the list!
                </h2>
                <p className="text-sm text-[#4A5568] leading-relaxed mb-2">
                  A confirmation email is on its way to <strong className="text-[#0A1128]">{form.email}</strong>.
                </p>
                <p className="text-sm text-[#4A5568] leading-relaxed mb-8">
                  We look forward to seeing you on {eventDate}.
                </p>
                <button
                  onClick={onClose}
                  className="inline-flex items-center gap-2 bg-[#CBA365] text-[#0A1128] px-8 py-3 text-xs uppercase tracking-[0.2em] transition-all duration-300 hover:bg-[#D4B97A]"
                  style={{ fontFamily: "'Outfit', sans-serif", fontWeight: 500 }}
                  data-testid="rsvp-close-button"
                >
                  Close
                </button>
              </div>
            ) : (
              /* Form state */
              <>
                <div className="mb-6">
                  <p className="overline mb-2">RSVP</p>
                  <h2
                    id="rsvp-modal-title"
                    className="section-heading font-heading text-2xl sm:text-3xl text-[#0A1128] mb-1"
                    style={{ letterSpacing: "-0.01em", lineHeight: 1.1 }}
                  >
                    {event.title}
                  </h2>
                  <p style={{ fontFamily: "'Outfit', sans-serif", fontSize: '0.85rem', color: 'rgba(10, 25, 41, 0.55)' }}>
                    {eventDate}{event.time ? ` \u00B7 ${event.time}` : ""}{event.end_time ? ` \u2013 ${event.end_time}` : ""}
                  </p>
                </div>

                {serverError && (
                  <div
                    className="mb-6 p-4 bg-red-50 border border-red-200 text-red-700 text-sm"
                    role="alert"
                    aria-live="polite"
                    data-testid="rsvp-error"
                  >
                    {serverError}
                  </div>
                )}

                <form onSubmit={handleSubmit} className="space-y-4" data-testid="rsvp-form">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label htmlFor="rsvp-first" className={labelClass}>First Name *</label>
                      <input
                        ref={firstInputRef}
                        id="rsvp-first"
                        type="text"
                        value={form.first_name}
                        onChange={update("first_name")}
                        className={inputClass}
                        disabled={status === "submitting"}
                        data-testid="rsvp-first-name"
                      />
                      {errors.first_name && <p className="text-red-500 text-xs mt-1">{errors.first_name}</p>}
                    </div>
                    <div>
                      <label htmlFor="rsvp-last" className={labelClass}>Last Name *</label>
                      <input
                        id="rsvp-last"
                        type="text"
                        value={form.last_name}
                        onChange={update("last_name")}
                        className={inputClass}
                        disabled={status === "submitting"}
                        data-testid="rsvp-last-name"
                      />
                      {errors.last_name && <p className="text-red-500 text-xs mt-1">{errors.last_name}</p>}
                    </div>
                  </div>

                  <div>
                    <label htmlFor="rsvp-email" className={labelClass}>Email *</label>
                    <input
                      id="rsvp-email"
                      type="email"
                      value={form.email}
                      onChange={update("email")}
                      className={inputClass}
                      disabled={status === "submitting"}
                      data-testid="rsvp-email"
                    />
                    {errors.email && <p className="text-red-500 text-xs mt-1">{errors.email}</p>}
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label htmlFor="rsvp-phone" className={labelClass}>Phone</label>
                      <input
                        id="rsvp-phone"
                        type="tel"
                        value={form.phone}
                        onChange={update("phone")}
                        className={inputClass}
                        disabled={status === "submitting"}
                        data-testid="rsvp-phone"
                      />
                    </div>
                    <div>
                      <label htmlFor="rsvp-guests" className={labelClass}>Total Guests</label>
                      <select
                        id="rsvp-guests"
                        value={form.guest_count}
                        onChange={update("guest_count")}
                        className={inputClass}
                        disabled={status === "submitting"}
                        data-testid="rsvp-guest-count"
                      >
                        {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((n) => (
                          <option key={n} value={n}>{n} {n === 1 ? "guest" : "guests"}</option>
                        ))}
                      </select>
                    </div>
                  </div>

                  {form.guest_count > 1 && (
                    <div className="space-y-3">
                      {Array.from({ length: form.guest_count - 1 }, (_, i) => (
                        <div key={i}>
                          <label htmlFor={`guest-${i}`} className={labelClass}>Guest {i + 2} Name</label>
                          <input
                            id={`guest-${i}`}
                            type="text"
                            value={form.guest_names[i] || ""}
                            onChange={updateGuestName(i)}
                            className={inputClass}
                            disabled={status === "submitting"}
                            placeholder="Optional"
                          />
                        </div>
                      ))}
                    </div>
                  )}

                  {event.food_served && (
                    <div>
                      <label htmlFor="rsvp-food-allergies" className={labelClass}>
                        Food allergies or dietary restrictions
                      </label>
                      <textarea
                        id="rsvp-food-allergies"
                        rows={2}
                        value={form.food_allergies}
                        onChange={update("food_allergies")}
                        className={`${inputClass} resize-none`}
                        disabled={status === "submitting"}
                        placeholder="Let us know of any allergies or dietary needs (vegetarian, gluten-free, etc.)"
                        data-testid="rsvp-food-allergies"
                      />
                    </div>
                  )}

                  <div>
                    <label htmlFor="rsvp-notes" className={labelClass}>Notes</label>
                    <textarea
                      id="rsvp-notes"
                      rows={2}
                      value={form.notes}
                      onChange={update("notes")}
                      className={`${inputClass} resize-none`}
                      disabled={status === "submitting"}
                      placeholder="Anything we should know?"
                      data-testid="rsvp-notes"
                    />
                  </div>

                  <button
                    type="submit"
                    disabled={status === "submitting"}
                    className="w-full flex items-center justify-center gap-2 transition-all duration-300 hover:bg-[#D4B97A] disabled:opacity-60"
                    style={{
                      backgroundColor: '#CBA365',
                      color: '#0A1128',
                      fontFamily: "'Outfit', sans-serif",
                      fontWeight: 500,
                      fontSize: '0.72rem',
                      letterSpacing: '0.32em',
                      textTransform: 'uppercase',
                      padding: '0.95rem 2rem',
                    }}
                    data-testid="rsvp-submit"
                  >
                    {status === "submitting" ? (
                      <><Loader2 size={14} className="animate-spin" /> Submitting...</>
                    ) : (
                      "Confirm RSVP"
                    )}
                  </button>

                  <p className="text-[11px] text-[#8A94A6] leading-relaxed text-center">
                    By submitting this form, you agree that Rain Boy Fine Art may contact you about this event, news, and upcoming events, including reminders and updates.
                  </p>
                </form>
              </>
            )}
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}
